import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CountdownTimer from "./CountdownTimer";

const YANDEX_AFISHA_URL =
  "https://afisha.yandex.ru/moscow/musical-play/vechnye-shopen?source=place&refererSurface=selection";

/**
 * PromoSection — popup flotante pequeño en la esquina inferior derecha.
 * No ocupa toda la pantalla, no interfiere en el scroll, no tiene overlay oscuro.
 *
 * Props:
 *   visible (boolean): el popup se abre cuando visible=true (controlado desde LandingPage)
 *   onClose (function): se llama al hacer clic en X; el popup pasa a estado minimizado
 *   showMinimized (boolean): controla si el botón minimizado se renderiza (true solo tras enviar formulario)
 *
 * Comportamiento:
 *   - Aparece expandido solo cuando `visible` es true.
 *   - Al cerrar con X, pasa a estado minimizado (botón flotante naranja).
 *   - Desde minimizado se puede reabrir haciendo clic en el botón.
 *   - Nunca desaparece completamente (solo si expira la cuenta regresiva).
 */
export default function PromoSection({ visible, onClose, showMinimized }) {
  const [copied, setCopied] = useState(false);
  const [expired, setExpired] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [reopened, setReopened] = useState(false);
  const promoCode = "ШОПЕН";

  // La cuenta regresiva empieza desde 15 minutos
  const targetDate = useMemo(() => {
    return new Date(Date.now() + 15 * 60 * 1000).toISOString();
  }, []);

  // Cuando visible se vuelve true, nos aseguramos de que no esté minimizado
  useEffect(() => {
    if (visible) {
      setMinimized(false);
      setReopened(false);
    }
  }, [visible]);

  // isOpen controla si el popup expandido se muestra
  const isOpen = (visible || reopened) && !minimized;

  const handleMinimize = () => {
    setMinimized(true);
    if (visible && onClose) onClose();
  };

  const handleReopen = () => {
    setMinimized(false);
    setReopened(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promoCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = promoCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyAndBuy = async () => {
    try {
      await navigator.clipboard.writeText(promoCode);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = promoCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    window.open(YANDEX_AFISHA_URL, "_blank");
  };

  if (expired) return null;

  return (
    <>
      {/* Popup expandido */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="promo-popup"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 z-50 max-w-[320px] w-full bg-[#120F0D] border border-[rgba(229,115,36,0.3)] rounded-xl shadow-2xl p-5 sm:right-6 left-4 right-4 mx-auto sm:left-auto"
          >
            {/* Botón de cerrar (X) — minimiza en lugar de desaparecer */}
            <button
              onClick={handleMinimize}
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center text-[#A99F96] hover:text-[#E57324] transition-colors text-xl font-light leading-none"
              aria-label="Свернуть"
            >
              ✕
            </button>

            {/* Título */}
            <h2
              className="font-serif font-light leading-[0.95] tracking-tight text-center"
              style={{
                fontSize: "clamp(1.6rem, 4vw, 2.2rem)",
                color: "#F2E9DE",
              }}
            >
              ПРОМОКОД
              <br />
              <em style={{ color: "#E57324" }}>ДЛЯ ВАС!</em>
            </h2>

            {/* Código promocional */}
            <div className="flex flex-col items-center gap-3 mt-4 mb-3">
              <div
                className="relative px-6 py-2.5 border-2 border-dashed rounded-sm w-full text-center"
                style={{
                  borderColor: "#E57324",
                  backgroundColor: "#1A1510",
                  boxShadow: "0 0 20px rgba(229,115,36,0.12), inset 0 0 20px rgba(229,115,36,0.03)",
                }}
              >
                <span
                  className="font-serif font-bold tracking-[0.3em] uppercase"
                  style={{
                    fontSize: "clamp(1.3rem, 4vw, 1.8rem)",
                    color: "#FFB84D",
                    textShadow: "0 0 25px rgba(255,184,77,0.25)",
                  }}
                >
                  {promoCode}
                </span>
                <div
                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 text-[0.65rem] uppercase tracking-[0.2em] font-semibold whitespace-nowrap"
                  style={{
                    backgroundColor: "#E57324",
                    color: "#F2E9DE",
                    fontFamily: "Outfit, sans-serif",
                  }}
                >
                  СКИДКА 20%
                </div>
              </div>

              {/* Botón copiar */}
              <button
                onClick={handleCopy}
                className="w-full py-2.5 text-sm tracking-wider uppercase transition-all duration-300"
                style={{
                  fontFamily: "Outfit, sans-serif",
                  color: copied ? "#22C55E" : "#E57324",
                  border: `1px solid ${copied ? "#22C55E" : "#E57324"}`,
                  backgroundColor: copied
                    ? "rgba(34,197,94,0.08)"
                    : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (!copied) {
                    e.target.style.backgroundColor = "rgba(229,115,36,0.1)";
                    e.target.style.boxShadow = "0 0 12px rgba(229,115,36,0.2)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!copied) {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.boxShadow = "none";
                  }
                }}
              >
                {copied ? "СКОПИРОВАНО ✓" : "Скопировать код"}
              </button>

              {/* Botón "Билеты со скидкой" */}
              <button
                onClick={handleCopyAndBuy}
                className="w-full py-2.5 text-sm tracking-wider uppercase transition-all duration-300 flex items-center justify-center gap-2"
                style={{
                  fontFamily: "Outfit, sans-serif",
                  color: "#080605",
                  backgroundColor: "#E57324",
                  border: "1px solid #E57324",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#F08A46";
                  e.target.style.boxShadow = "0 0 16px rgba(229,115,36,0.35)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#E57324";
                  e.target.style.boxShadow = "none";
                }}
              >
                Билеты со скидкой →
              </button>
            </div>

            {/* Instrucción */}
            <p
              className="text-[0.6rem] leading-relaxed text-[#A99F96] text-center"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              ВВЕДИТЕ ПРОМОКОД ПРИ ОПЛАТЕ ПОСЛЕ ВЫБОРА МЕСТ В ГРАФУ «ПРОМОКОД»
            </p>

            {/* Countdown */}
            <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-[#F2E9DE]/10">
              <span
                className="text-[0.68rem] tracking-[0.3em] uppercase text-[#A99F96]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                ОСТАЛОСЬ
              </span>
              <CountdownTimer
                targetDate={targetDate}
                onExpired={() => setExpired(true)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Botón flotante minimizado — solo visible después de enviar el formulario (showMinimized=true) */}
      <AnimatePresence>
        {!isOpen && !expired && showMinimized && (
          <motion.button
            key="promo-minimized"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={handleReopen}
            className="fixed bottom-6 right-6 z-50 flex items-center justify-center cursor-pointer group"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: "#E57324",
              boxShadow: "0 4px 20px rgba(229,115,36,0.4)",
              border: "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 6px 28px rgba(229,115,36,0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(229,115,36,0.4)";
            }}
            aria-label="Открыть промокод"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="5" width="20" height="14" rx="2" stroke="#080605" strokeWidth="1.5" />
              <path d="M8 5V19" stroke="#080605" strokeWidth="1.5" />
              <path d="M16 5V19" stroke="#080605" strokeWidth="1.5" />
              <rect x="8" y="11" width="8" height="2" fill="#080605" rx="0.5" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
